import type { Notebook } from "@engram/core";
import { Book, BookCopy, BookPlus, ChevronDown, ChevronRight, FileText, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { createNotebook, notebookNameError } from "../db/actions";
import { useQuery } from "../db/client";
import { collator } from "./format";
import { Prompt } from "./Menu";
import { buildTree } from "./notebookTree";

/**
 * Evernote's Move / Copy to dialog: "Move [note]" with a location search, "New notebook", the notebooks (stacks
 * expand), and Cancel / Done. Done stays disabled until a different notebook is picked.
 */
export function LocationDialog({
  title,
  subject,
  current,
  onPick,
  onClose,
}: {
  title: string;
  subject: string;
  current?: string;
  onPick: (notebookId: string) => void;
  onClose: () => void;
}) {
  const rows = useQuery<Notebook>("SELECT * FROM notebooks WHERE deleted = 0", [], ["notebooks"]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [creating, setCreating] = useState(false);

  const term = q.trim().toLowerCase();
  const items = useMemo(() => {
    const all = rows ?? [];
    const byName = (a: Notebook, b: Notebook) => collator.compare(a.name, b.name);
    if (term) return all.filter((b) => b.kind === "notebook" && b.name.toLowerCase().includes(term)).sort(byName).map((nb) => ({ nb, depth: 0 }));
    const tree = buildTree(all);
    const walk = (r: Notebook, depth: number): { nb: Notebook; depth: number }[] =>
      r.kind === "stack" ? [{ nb: r, depth }, ...(closed[r.id] ? [] : tree.children(r.id).flatMap((c) => walk(c, depth + 1)))] : [{ nb: r, depth }];
    return tree.top.flatMap((r) => walk(r, 0));
  }, [rows, term, closed]);

  const done = (id = picked) => {
    if (!id || id === current) return;
    onPick(id);
    onClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal location-dialog"
        role="dialog"
        aria-label={title}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          else if (e.key === "Enter" && !creating && (e.target as HTMLElement).tagName === "INPUT") {
            e.preventDefault();
            done(picked ?? (term ? items.find((i) => i.nb.kind === "notebook")?.nb.id : null) ?? null);
          }
        }}
      >
        <div className="location-head">
          <h2>{title}</h2>
          <span className="chip location-subject">
            <FileText size={13} />
            <span className="chip-text">{subject || "Untitled"}</span>
          </span>
          <div className="grow" />
          <button className="icon-btn" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </div>
        <label className="find-input location-find">
          <input autoFocus placeholder="Find a location…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Search size={14} />
        </label>
        <div className="location-list" role="listbox" aria-label="Notebooks">
          {!term && (
            <button className="location-row create" onClick={() => setCreating(true)}>
              <BookPlus size={15} className="ic-create" />
              <span>New Notebook</span>
            </button>
          )}
          {items.map(({ nb, depth }) =>
            nb.kind === "stack" ? (
              <button key={nb.id} className="location-row stack" style={depth ? { paddingLeft: 12 + depth * 22 } : undefined} onClick={() => setClosed((c) => ({ ...c, [nb.id]: !c[nb.id] }))}>
                {closed[nb.id] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                <BookCopy size={15} />
                <span>{nb.name}</span>
              </button>
            ) : (
              <button
                key={nb.id}
                role="option"
                aria-selected={picked === nb.id}
                className={`location-row${picked === nb.id ? " picked" : ""}${nb.id === current ? " current" : ""}`}
                style={{ paddingLeft: 12 + depth * 22 }}
                onClick={() => setPicked(nb.id === current ? null : nb.id)}
                onDoubleClick={() => done(nb.id)}
              >
                <Book size={15} />
                <span>{nb.name}</span>
                {nb.id === current && <span className="muted">(current)</span>}
              </button>
            ),
          )}
          {term && items.length === 0 && <div className="menu-empty">No notebooks match</div>}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!picked} onClick={() => done()}>Done</button>
        </div>
      </div>
      {creating && (
        <Prompt
          title="Create new notebook"
          label="Name"
          confirm="Create"
          validate={notebookNameError("notebook")}
          onSubmit={async (v) => setPicked(await createNotebook(v))}
          onClose={() => setCreating(false)}
        />
      )}
    </div>,
    document.body,
  );
}
